import { supabase } from '../supabase';
import type { Database } from '../database.types';

type Client = Database['public']['Tables']['clients']['Row'];
type ClientInsert = Database['public']['Tables']['clients']['Insert'];
type ClientUpdate = Database['public']['Tables']['clients']['Update'];

export interface ClientWithRelationship extends Client {
  agent_client_relationships?: {
    id: string;
    status: string;
    assigned_date: string;
    notes: string | null;
  }[];
}

export interface ClientStats {
  total: number;
  active: number;
  hotLeads: number;
  new: number;
  touring: number;
  offer: number;
  underContract: number;
  closed: number;
  inactive: number;
}

export class ClientAPI {
  // Get all clients for an agent
  static async getAgentClients(agentId: string): Promise<ClientWithRelationship[]> {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        agent_client_relationships!inner(
          id,
          status,
          assigned_date,
          notes
        )
      `)
      .eq('agent_client_relationships.agent_id', agentId)
      .eq('agent_client_relationships.status', 'active')
      .order('last_contact', { ascending: false });

    if (error) {
      console.error('Error fetching agent clients:', error);
      throw error;
    }

    return data || [];
  }

  // Get client statistics for an agent
  static async getClientStats(agentId: string): Promise<ClientStats> {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        stage,
        agent_client_relationships!inner(agent_id)
      `)
      .eq('agent_client_relationships.agent_id', agentId)
      .eq('agent_client_relationships.status', 'active');

    if (error) {
      console.error('Error fetching client stats:', error);
      throw error;
    }

    const stats: ClientStats = {
      total: data?.length || 0,
      active: 0,
      hotLeads: 0,
      new: 0,
      touring: 0,
      offer: 0,
      underContract: 0,
      closed: 0,
      inactive: 0
    };

    data?.forEach(client => {
      switch (client.stage) {
        case 'new':
          stats.new++;
          break;
        case 'active':
          stats.active++;
          break;
        case 'touring':
          stats.touring++;
          break;
        case 'offer':
          stats.offer++;
          stats.hotLeads++;
          break;
        case 'under_contract':
          stats.underContract++;
          stats.hotLeads++;
          break;
        case 'closed':
          stats.closed++;
          break;
        case 'inactive':
          stats.inactive++;
          break;
      }
    });

    return stats;
  }

  // Create a new client
  static async createClient(clientData: ClientInsert): Promise<Client> {
    const { data, error } = await supabase
      .from('clients')
      .insert(clientData)
      .select()
      .single();

    if (error) {
      console.error('Error creating client:', error);
      throw error;
    }

    return data;
  }

  // Update client information
  static async updateClient(clientId: string, updates: ClientUpdate): Promise<Client> {
    const { data, error } = await supabase
      .from('clients')
      .update(updates)
      .eq('id', clientId)
      .select()
      .single();

    if (error) {
      console.error('Error updating client:', error);
      throw error;
    }

    return data;
  }

  // Update client stage
  static async updateClientStage(clientId: string, stage: Client['stage']): Promise<Client> {
    return this.updateClient(clientId, { stage });
  }

  // Update client next action
  static async updateClientNextAction(clientId: string, nextAction: string): Promise<Client> {
    return this.updateClient(clientId, { next_action: nextAction });
  }

  // Update last contact
  static async updateLastContact(clientId: string): Promise<Client> {
    return this.updateClient(clientId, { last_contact: new Date().toISOString() });
  }

  // Search clients
  static async searchClients(agentId: string, query: string): Promise<ClientWithRelationship[]> {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        agent_client_relationships!inner(
          id,
          status,
          assigned_date,
          notes
        )
      `)
      .eq('agent_client_relationships.agent_id', agentId)
      .eq('agent_client_relationships.status', 'active')
      .or(`name.ilike.%${query}%,email.ilike.%${query}%,phone.ilike.%${query}%`)
      .order('last_contact', { ascending: false });

    if (error) {
      console.error('Error searching clients:', error);
      throw error;
    }

    return data || [];
  }

  // Filter clients by type and stage
  static async filterClients(
    agentId: string,
    filters: {
      type?: 'buyer' | 'renter' | 'seller';
      stage?: Client['stage'];
      sortBy?: 'name' | 'lastContact' | 'stage';
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<ClientWithRelationship[]> {
    let query = supabase
      .from('clients')
      .select(`
        *,
        agent_client_relationships!inner(
          id,
          status,
          assigned_date,
          notes
        )
      `)
      .eq('agent_client_relationships.agent_id', agentId)
      .eq('agent_client_relationships.status', 'active');

    if (filters.type) {
      query = query.eq('type', filters.type);
    }

    if (filters.stage) {
      query = query.eq('stage', filters.stage);
    }

    // Apply sorting
    if (filters.sortBy) {
      const sortField = filters.sortBy === 'lastContact' ? 'last_contact' : filters.sortBy;
      query = query.order(sortField, { ascending: filters.sortOrder === 'asc' });
    } else {
      query = query.order('last_contact', { ascending: false });
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error filtering clients:', error);
      throw error;
    }

    return data || [];
  }

  // Create client from property inquiry
  static async createFromInquiry(
    name: string,
    email: string,
    phone: string,
    message: string,
    propertyId: string,
    agentId: string
  ): Promise<Client> {
    const { data, error } = await supabase.rpc('create_client_from_inquiry', {
      p_name: name,
      p_email: email,
      p_phone: phone,
      p_message: message,
      p_property_id: propertyId,
      p_agent_id: agentId
    });

    if (error) {
      console.error('Error creating client from inquiry:', error);
      throw error;
    }

    // Fetch the created client
    const { data: client, error: fetchError } = await supabase
      .from('clients')
      .select('*')
      .eq('id', data)
      .single();

    if (fetchError) {
      console.error('Error fetching created client:', fetchError);
      throw fetchError;
    }

    return client;
  }
}
